/**
 * 空域を使う課題（APT / 3DT）の純粋計算。
 *
 * 対象は AXMER 2026 Chapter 15 の次の2つ:
 *
 *   15.21 APT (ALTITUDE PROFILE TASK / 高度プロファイルタスク)
 *     与えられた高度プロファイルの周囲に1つまたは2つの帯を置き、
 *     内側の帯にいた時間は2倍、外側の帯は1倍、帯の外は0として累積する。
 *     タスクは電子マークで開始される。累積時間が大きいほど良い。
 *
 *   15.20 3DT (3D SHAPE TASK / 3Dシェイプタスク)
 *     設定された空域内で最大距離を目指す。結果は空域内の有効な
 *     トラックポイント間の累積**水平**距離。大きいほど良い。
 *
 * ★★ ここで出す数字は**飛行支援のための参考値**であって公式スコアでは
 *   ない。公式の採点は主催者の採点ソフトが行う。トラックポイントの
 *   有効性判定・丸め・タイミングの細部は大会規則側にあり、ここでは
 *   再現していない。UI では必ず「参考値」と分かる形で出すこと。
 *
 * 帯幅・プロファイル・空域の形はすべて**呼び出し側（データ）が持つ**。
 * notam.ts と同じ方針で、特定の大会の値をここへ焼き込まない
 * （規定の例示値 50ft / 150ft も既定値として置くだけで、上書きできる）。
 */

import type { LatLon } from './dms.ts';
import { pointInPolygon } from './notam.ts';

// --- APT（高度プロファイルタスク） -------------------------------------------

/** 高度プロファイルの節点。タスク開始からの経過時間と目標高度 */
export interface AltitudeProfilePoint {
  /** タスク開始（電子マーク）からの経過秒 */
  tSec: number;
  /** 目標高度 [ft] */
  altFt: number;
}

/**
 * 規定 15.21 の例示値。内側帯A=±50ft（2倍）、外側帯B=±150ft（1倍）。
 * 参考図（apt-sketch.svg）の凡例もこの値になっている。
 * 大会ごとに変わりうるので、あくまで既定値として置くだけにする。
 */
export const DEFAULT_APT_INNER_BAND_FT = 50;
export const DEFAULT_APT_OUTER_BAND_FT = 150;

export interface AptTask {
  /** 目標高度プロファイル。tSec の昇順に並んでいること */
  profile: AltitudeProfilePoint[];
  /** 内側帯A（結果2倍）の半幅 [ft] */
  innerBandFt: number;
  /** 外側帯B（結果1倍）の外縁までの半幅 [ft] */
  outerBandFt: number;
}

/** 帯の重み。規定 15.21 の「内側2倍 / 外側1倍 / 外は数えない」 */
export type AptBand = 'inner' | 'outer' | 'outside';

export function aptBandWeight(band: AptBand): number {
  if (band === 'inner') return 2;
  if (band === 'outer') return 1;
  return 0;
}

/**
 * プロファイル上の、経過時間 tSec における目標高度 [ft]。
 *
 * 節点の間は線形補間する（参考図の帯が節点間で斜めに繋がっているため。
 * 階段状ではない）。プロファイルの時間範囲の外は null を返す
 * ＝タスクが定義されていない時間帯なので採点しない。
 */
export function aptTargetAltFt(task: AptTask, tSec: number): number | null {
  const p = task.profile;
  if (p.length === 0) return null;
  if (tSec < p[0].tSec || tSec > p[p.length - 1].tSec) return null;
  for (let i = 0; i + 1 < p.length; i++) {
    const a = p[i];
    const b = p[i + 1];
    if (tSec >= a.tSec && tSec <= b.tSec) {
      if (b.tSec === a.tSec) return a.altFt;
      const ratio = (tSec - a.tSec) / (b.tSec - a.tSec);
      return a.altFt + (b.altFt - a.altFt) * ratio;
    }
  }
  return p[p.length - 1].altFt;
}

/**
 * 経過時間 tSec・高度 altFt がどの帯にいるか。
 * プロファイルの範囲外、または帯の外なら 'outside'。
 *
 * 境界ちょうどは内側（有利な側）に含める。規定は境界の扱いを明記して
 * いないが、これは参考表示であり、境界で表示がちらつくより「入っている」
 * と見せたほうが操作の助けになるため。
 */
export function aptBandAt(task: AptTask, tSec: number, altFt: number): AptBand {
  const target = aptTargetAltFt(task, tSec);
  if (target == null) return 'outside';
  const diff = Math.abs(altFt - target);
  if (diff <= task.innerBandFt) return 'inner';
  if (diff <= task.outerBandFt) return 'outer';
  return 'outside';
}

/** APT の採点に渡すトラックポイント */
export interface AptTrackPoint {
  /** UTC epoch ms */
  t: number;
  /** 高度 [ft] */
  altFt: number;
}

export interface AptScoreOptions {
  /** タスク開始（電子マーク）の時刻 [UTC epoch ms] */
  startMs: number;
  /**
   * この秒数を超えるトラックの欠落は積算しない。
   * 受信が途切れた間の飛び方は分からないので、繋いで数えると
   * 実際より良い値が出てしまう。既定30秒。
   */
  maxGapSec?: number;
  /** 積分の刻み [秒]。既定1秒 */
  stepSec?: number;
}

export interface AptScore {
  /** 重み付き累積時間 [秒]（内側は2倍で数えたもの）。これが結果 */
  weightedSec: number;
  /** 内側帯にいた実時間 [秒] */
  innerSec: number;
  /** 外側帯にいた実時間 [秒] */
  outerSec: number;
  /** 帯の外にいた実時間 [秒]（プロファイル範囲内のみ） */
  outsideSec: number;
  /** 欠落が大きすぎて数えなかった時間 [秒]。参考値の信頼度を示すために出す */
  skippedSec: number;
}

/**
 * APT の累積時間を求める（参考値）。
 *
 * トラックポイントは間隔がまちまちなので、隣り合う2点の間を stepSec 刻みで
 * 線形補間しながら帯を判定して積算する。単純に「点ごとの帯 × 間隔」で
 * 数えると、帯をまたぐ区間の扱いが粗くなりすぎるため。
 */
export function scoreApt(
  task: AptTask,
  points: readonly AptTrackPoint[],
  options: AptScoreOptions,
): AptScore {
  const maxGapSec = options.maxGapSec ?? 30;
  const stepSec = Math.max(0.1, options.stepSec ?? 1);
  const out: AptScore = {
    weightedSec: 0,
    innerSec: 0,
    outerSec: 0,
    outsideSec: 0,
    skippedSec: 0,
  };
  if (task.profile.length === 0) return out;

  // 時刻順に並べ替えてから見る（受信順が前後することがある）
  const sorted = [...points].sort((a, b) => a.t - b.t);

  for (let i = 0; i + 1 < sorted.length; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    const gapSec = (b.t - a.t) / 1000;
    if (gapSec <= 0) continue;
    if (gapSec > maxGapSec) {
      out.skippedSec += gapSec;
      continue;
    }

    for (let s = 0; s < gapSec; s += stepSec) {
      const chunk = Math.min(stepSec, gapSec - s);
      const ratio = (s + chunk / 2) / gapSec; // 小片の中央で判定する
      const t = a.t + (b.t - a.t) * ratio;
      const altFt = a.altFt + (b.altFt - a.altFt) * ratio;
      const elapsedSec = (t - options.startMs) / 1000;
      const band = aptBandAt(task, elapsedSec, altFt);
      if (band === 'inner') out.innerSec += chunk;
      else if (band === 'outer') out.outerSec += chunk;
      else if (aptTargetAltFt(task, elapsedSec) != null) out.outsideSec += chunk;
      out.weightedSec += aptBandWeight(band) * chunk;
    }
  }

  return out;
}

// --- 3DT（3Dシェイプタスク） --------------------------------------------------

/**
 * 設定空域1つぶん。水平方向は多角形、垂直方向は下限・上限で挟む。
 * 上限・下限の null は「制限なし」（地面まで／無制限）を表す。
 */
export interface AirspaceVolume {
  label: string;
  /** 開いた頂点列（notam.ts の NotamZone と同じ規約。描画時に始点へ戻す） */
  points: LatLon[];
  /** 下限高度 [ft MSL]。null なら地面まで */
  floorFt: number | null;
  /** 上限高度 [ft MSL]。null なら無制限 */
  ceilingFt: number | null;
}

/** 点が空域の中にあるか（水平の多角形内 かつ 上下限の間） */
export function isInsideVolume(
  volume: AirspaceVolume,
  point: LatLon & { altFt: number },
): boolean {
  if (volume.floorFt != null && point.altFt < volume.floorFt) return false;
  if (volume.ceilingFt != null && point.altFt > volume.ceilingFt) return false;
  return pointInPolygon({ lat: point.lat, lon: point.lon }, volume.points);
}

/** 3DT の採点に渡すトラックポイント */
export interface VolumeTrackPoint {
  t: number;
  lat: number;
  lon: number;
  /** 高度 [ft] */
  altFt: number;
}

/** 2点間の水平距離 [m]（局所平面近似。会場スケールでは十分） */
export function horizontalDistanceM(a: LatLon, b: LatLon): number {
  const midLatRad = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  const dx = (b.lon - a.lon) * 111320 * Math.cos(midLatRad);
  const dy = (b.lat - a.lat) * 110574;
  return Math.hypot(dx, dy);
}

export interface ThreeDScore {
  /** 空域内の累積水平距離 [m]。これが結果 */
  distanceM: number;
  /** 空域内にいたトラックポイントの数 */
  pointsInside: number;
}

/**
 * 3DT の累積水平距離を求める（参考値）。
 *
 * 規定 15.20.3 は「空域内の有効なトラックポイント間の累積水平距離」。
 * **両端が空域内にある区間だけ**を足す（片側だけ内側の区間は、境界を
 * どこで跨いだかが分からないので数えない＝安全側に少なく見積もる）。
 * 空域が複数ある場合、区間の両端が同じ空域に入っていれば数える。
 */
export function scoreThreeD(
  volumes: readonly AirspaceVolume[],
  points: readonly VolumeTrackPoint[],
): ThreeDScore {
  const out: ThreeDScore = { distanceM: 0, pointsInside: 0 };
  if (volumes.length === 0) return out;

  const sorted = [...points].sort((a, b) => a.t - b.t);
  // 各点がどの空域に入っているか（インデックスの集合）を先に出す
  const membership = sorted.map((p) => {
    const set = new Set<number>();
    for (const [i, v] of volumes.entries()) if (isInsideVolume(v, p)) set.add(i);
    return set;
  });

  for (const [i, set] of membership.entries()) {
    if (set.size > 0) out.pointsInside++;
    if (i === 0) continue;
    const prev = membership[i - 1];
    // 同じ空域に連続して入っているか
    let shared = false;
    for (const v of set) {
      if (prev.has(v)) {
        shared = true;
        break;
      }
    }
    if (shared) out.distanceM += horizontalDistanceM(sorted[i - 1], sorted[i]);
  }

  return out;
}

// --- 空中ゴール（高度付きゴールの判定容積） -----------------------------------

/**
 * 空中ゴールの判定容積。
 *
 * ★ AXMER Chapter 15 の PDG（15.1）には高度の記述が無く、空中ゴールの
 *   許容範囲は各大会のタスクデータ側で決まる。したがって寸法をここへ
 *   焼き込まず、呼び出し側（データ・UI入力）が持つ形にする。
 *   「半径」と「上下の許容差」を持つ円柱として表す。
 */
export interface AerialGoalVolume {
  lat: number;
  lon: number;
  /** 目標高度 [ft MSL] */
  altFt: number;
  /** 水平の許容半径 [m] */
  radiusM: number;
  /** 上下の許容差 [ft]（目標高度の上下それぞれ） */
  altToleranceFt: number;
}

/** 点が空中ゴールの判定容積の中にあるか */
export function isInAerialGoal(
  goal: AerialGoalVolume,
  point: { lat: number; lon: number; altFt: number },
): boolean {
  if (Math.abs(point.altFt - goal.altFt) > goal.altToleranceFt) return false;
  return horizontalDistanceM(goal, point) <= goal.radiusM;
}
